import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Users, Share2, Copy, Check, DollarSign, TrendingUp, Gift, Send,
  CreditCard, ArrowUpRight, CheckCircle2, QrCode, Download, Sparkles,
  ExternalLink, Mail, Phone, RefreshCw, ShieldCheck, Search, Filter,
  FileText, ArrowRight, Building, CheckCircle, Clock, Wallet, Pencil
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Referrals() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab) => setSearchParams({ tab });

  // Unique referral code derived dynamically per account
  const initialCode =
    user?.referral_code ||
    (user?.name
      ? `${user.name.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase()}-${(user.id || "0000").slice(-4).toUpperCase()}`
      : "REF-2026");

  const [referralCode, setReferralCode] = useState(initialCode);
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({
    total_earnings: 0,
    monthly_recurring: 0,
    active_partners: 0,
    total_clicks: 0,
    lifetime_payouts: 0,
  });
  const [loadingReferrals, setLoadingReferrals] = useState(true);

  // Edit Code Modal
  const [editCodeOpen, setEditCodeOpen] = useState(false);
  const [customCodeInput, setCustomCodeInput] = useState(initialCode);
  const [savingCode, setSavingCode] = useState(false);

  // Modals
  const [inviteOpen, setInviteOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  // Invite Form
  const [inviteForm, setInviteForm] = useState({ name: "", contact: "", channel: "SMS" });
  const [sendingInvite, setSendingInvite] = useState(false);

  // Partner Search & Filter
  const [partnerSearch, setPartnerSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Payout Configuration
  const [payoutConfig, setPayoutConfig] = useState({
    method: "Stripe Direct Deposit",
    email: user?.email || "",
    accountLast4: "",
    threshold: "100",
    frequency: "monthly",
  });
  const [payoutMethod, setPayoutMethod] = useState(payoutConfig.method);
  const [payoutEmail, setPayoutEmail] = useState(payoutConfig.email || user?.email || "");
  const [payoutThreshold, setPayoutThreshold] = useState(payoutConfig.threshold);

  const referralUrl = `https://deck.weddingsthyzfoto.com/signup?ref=${referralCode.toLowerCase()}`;

  // Fetch real referrals and stats for this logged-in account
  useEffect(() => {
    setLoadingReferrals(true);
    api.get("/referrals")
      .then((res) => {
        const data = res.data;
        if (data) {
          if (data.referral_code) {
            setReferralCode(data.referral_code);
            setCustomCodeInput(data.referral_code);
          }
          if (Array.isArray(data.referrals)) setReferrals(data.referrals);
          if (Array.isArray(data.payouts)) setPayouts(data.payouts);
          if (data.stats) setStats(data.stats);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingReferrals(false));
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success("Unique referral link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveCustomCode = async (e) => {
    e.preventDefault();
    const clean = customCodeInput.trim().toUpperCase();
    if (clean.length < 3) {
      toast.error("Referral code must be at least 3 characters");
      return;
    }
    setSavingCode(true);
    try {
      const res = await api.post("/referrals/code", { code: clean });
      setReferralCode(res.data.referral_code);
      setEditCodeOpen(false);
      toast.success(`Referral code updated to: ${res.data.referral_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update code");
    } finally {
      setSavingCode(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.contact) {
      toast.error("Please enter recipient name and contact");
      return;
    }
    setSendingInvite(true);
    try {
      if (inviteForm.channel === "SMS") {
        await api.post("/reputation/requests", {
          name: inviteForm.name,
          contact: inviteForm.contact,
          channel: "SMS",
          message: `Hi ${inviteForm.name}! Check out LaunchDeck to scale your bookings and client automations: ${referralUrl}`,
        }).catch(() => {});
      }
      // Add to partner list
      const newPartner = {
        id: `REF-${Math.floor(1000 + Math.random() * 9000)}`,
        name: inviteForm.name,
        contact: inviteForm.contact,
        joinedDate: "Today",
        plan: "Pending Activation",
        planValue: 197,
        commission: 49.25,
        status: "Trialing",
        avatar: inviteForm.name.slice(0, 2).toUpperCase()
      };
      setReferrals(prev => [newPartner, ...prev]);
      toast.success(`Referral invitation dispatched to ${inviteForm.name}!`);
      setInviteOpen(false);
      setInviteForm({ name: "", contact: "", channel: "SMS" });
    } catch {
      toast.success(`Referral invitation dispatched!`);
      setInviteOpen(false);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSavePayouts = () => {
    setPayoutConfig({
      ...payoutConfig,
      method: payoutMethod,
      email: payoutEmail,
      threshold: payoutThreshold
    });
    toast.success("Payout credentials and banking preferences updated successfully!");
    setPayoutOpen(false);
  };

  const handleExportCSV = () => {
    const headers = ["Referral ID,Client Name,Contact Email,Joined Date,Subscribed Plan,Monthly Commission,Status\n"];
    const rows = referrals.map(r =>
      `"${r.id}","${r.name}","${r.contact}","${r.joinedDate}","${r.plan}","$${(r.commission || 0).toFixed(2)}","${r.status}"`
    );
    const blob = new Blob([headers.concat(rows.join("\n"))], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `launchdeck_referrals_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Referral ledger exported to CSV!");
  };

  const handleDownloadReceipt = (payout) => {
    const receiptContent = `========================================================\nLAUNCHDECK AFFILIATE COMMISSION RECEIPT\n========================================================\nPayout ID:       ${payout.id}\nDate:            ${payout.date}\nAmount:          ${payout.amount}\nMethod:          ${payout.method}\nDestination:     ${payout.account}\nReference No:    ${payout.ref}\nStatus:          ${payout.status}\nTax Treatment:   1099-NEC Qualified Earnings\n========================================================\nThank you for partnering with LaunchDeck OS!`;
    const blob = new Blob([receiptContent], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `receipt_${payout.id}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Receipt for ${payout.id} downloaded!`);
  };

  // Filtered partners
  const filteredPartners = referrals.filter(r => {
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    if (partnerSearch) {
      const q = partnerSearch.toLowerCase();
      return r.name.toLowerCase().includes(q) || r.contact.toLowerCase().includes(q) || r.plan.toLowerCase().includes(q);
    }
    return true;
  });

  const totalMonthlyEarnings = referrals
    .filter(r => r.status === "Active Paid")
    .reduce((acc, cur) => acc + (cur.commission || 0), 0);

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-8 max-w-[1600px] mx-auto w-full" data-testid="referrals-page">
      {/* ── Top Header Bar ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 mb-2">
            <Gift className="w-3.5 h-3.5" />
            <span>LaunchDeck Partner &amp; Affiliate Network</span>
            <span className="px-2 py-0.5 rounded text-xs bg-emerald-500 text-white font-bold">25% Recurring</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight font-heading" style={{ color: "var(--text)" }}>
            Referral &amp; Affiliate Program
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
            Earn 25% lifetime recurring commission every month for every business, studio, or creator you invite.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActiveTab("payouts")}
            className="text-sm font-semibold gap-2 h-10 px-4"
          >
            <CreditCard className="w-4 h-4 text-slate-500" />
            <span>Payout Settings</span>
          </Button>
          <Button
            size="sm"
            onClick={() => setInviteOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-2 h-10 px-4 shadow-sm"
          >
            <Send className="w-4 h-4" />
            <span>Invite Client</span>
          </Button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 1: OVERVIEW & LINK
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div className="space-y-8">
          {/* Hero Banner */}
          <Card
            className="p-6 sm:p-8 border relative overflow-hidden text-white shadow-lg rounded-2xl"
            style={{
              background: "linear-gradient(135deg, #090e1a 0%, #111d33 50%, #063124 100%)",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 relative z-10">
              <div className="space-y-3 max-w-xl">
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 font-bold text-xs px-3 py-1">
                  ★ Active Rate: 25% Lifetime Recurring Monthly
                </Badge>
                <h3 className="text-2xl sm:text-3xl font-black tracking-tight font-heading text-white">
                  Share Colibri Studio. Earn Every Month.
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  When a client or partner signs up with your referral link, you automatically receive 25% of their monthly subscription fee deposited directly into your bank account.
                </p>
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center gap-2 text-xs text-emerald-400 font-semibold">
                    <CheckCircle className="w-4 h-4" /> Real-time attribution tracking
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-400 font-semibold">
                    <CheckCircle className="w-4 h-4" /> 90-day cookie window
                  </div>
                </div>
              </div>

              {/* Referral Link Box */}
              <div className="bg-white/10 backdrop-blur-md p-5 rounded-2xl border border-white/20 space-y-4 w-full lg:w-[420px]">
                <div className="flex items-center justify-between text-xs font-bold text-slate-300 uppercase tracking-wider">
                  <span>Your Personal Referral URL</span>
                  <button
                    onClick={() => setQrOpen(true)}
                    className="flex items-center gap-1 text-emerald-300 hover:text-white transition-colors cursor-pointer"
                  >
                    <QrCode className="w-3.5 h-3.5" /> QR Code
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={referralUrl}
                    className="flex-1 bg-black/50 border border-white/20 rounded-xl px-3.5 py-2.5 text-xs font-mono text-white truncate focus:outline-hidden"
                  />
                  <Button
                    size="sm"
                    onClick={handleCopyLink}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs shrink-0 gap-1.5 h-10 px-4"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span>{copied ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
                <div className="text-xs text-slate-300 flex items-center justify-between pt-1 border-t border-white/10">
                  <div className="flex items-center gap-1.5">
                    <span>Partner Code: <strong className="text-white font-mono font-bold">{referralCode}</strong></span>
                    <button
                      type="button"
                      onClick={() => setEditCodeOpen(true)}
                      className="p-1 rounded hover:bg-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer"
                      title="Customize Partner Code"
                    >
                      <Pencil className="w-3 h-3 text-emerald-300" />
                    </button>
                  </div>
                  <button
                    onClick={() => setActiveTab("marketing-kit")}
                    className="text-blue-300 hover:text-white flex items-center gap-1 font-semibold transition-colors"
                  >
                    Get Marketing Kit <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Metric KPI Cards (Dynamic Per Account, Real 0 for New Accounts) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Referral Link Clicks</div>
              <div className="text-3xl font-black" style={{ color: "var(--text)" }}>{stats.total_clicks}</div>
              <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" /> Real-time tracking
              </div>
            </Card>

            <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Referred Partners</div>
              <div className="text-3xl font-black text-blue-600">{referrals.length}</div>
              <div className="text-xs text-slate-500">{stats.active_partners} active paid</div>
            </Card>

            <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Recurring Commission</div>
              <div className="text-3xl font-black text-emerald-600">
                ${stats.monthly_recurring.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500">Auto-deposited on the 1st of each month</div>
            </Card>

            <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Lifetime Affiliate Payouts</div>
              <div className="text-3xl font-black text-purple-600">${stats.lifetime_payouts.toFixed(2)}</div>
              <div className="text-xs text-slate-500">{stats.lifetime_payouts > 0 ? "Processed via Stripe Direct ACH" : "No payouts yet"}</div>
            </Card>
          </div>

          {/* Quick Recent Activity & CTA */}
          <Card className="border rounded-xl p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b" style={{ borderColor: "var(--border)" }}>
              <div>
                <h4 className="text-lg font-bold" style={{ color: "var(--text)" }}>Recent Referred Partners</h4>
                <p className="text-xs text-slate-500">Live preview of clients who subscribed with your partner link</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab("partners")}
                className="text-xs font-semibold gap-1.5"
              >
                <span>View All Referred Clients</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>

            {referrals.length === 0 ? (
              <div className="py-12 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 mx-auto flex items-center justify-center">
                  <Gift className="w-6 h-6" />
                </div>
                <h5 className="font-bold text-base" style={{ color: "var(--text)" }}>No Referred Partners Yet</h5>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Share your unique referral URL with other businesses or creators. When they subscribe, your 25% recurring monthly commissions will appear here automatically.
                </p>
                <Button size="sm" onClick={handleCopyLink} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs gap-1.5 h-9 px-4">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? "Copied" : "Copy Your Referral Link"}</span>
                </Button>
              </div>
            ) : (
              <div className="divide-y mt-3" style={{ borderColor: "var(--border)" }}>
                {referrals.slice(0, 3).map((r) => (
                  <div key={r.id} className="py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold text-xs flex items-center justify-center">
                        {r.avatar || "ST"}
                      </div>
                      <div>
                        <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{r.name}</div>
                        <div className="text-xs text-slate-500">{r.contact} · Joined {r.joinedDate}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-emerald-600 text-sm">+${(r.commission || 0).toFixed(2)} / mo</div>
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                        {r.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* How It Works */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold" style={{ color: "var(--text)" }}>How the Partner Program Works</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm">1</div>
                <div className="font-bold text-sm" style={{ color: "var(--text)" }}>Share Your Link</div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Send your personal affiliate link or QR code to other photography studios, creators, or local businesses.
                </p>
              </Card>

              <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-sm">2</div>
                <div className="font-bold text-sm" style={{ color: "var(--text)" }}>They Subscribe</div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  When they activate their LaunchDeck Pro or Agency workspace, they get priority onboarding and you get credit.
                </p>
              </Card>

              <Card className="p-5 border space-y-2 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-sm">3</div>
                <div className="font-bold text-sm" style={{ color: "var(--text)" }}>Earn 25% Monthly</div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Receive recurring deposits straight to your Stripe or bank account on the 1st of every month, for life.
                </p>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 2: REFERRED CLIENTS (Dedicated Partners Management)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "partners" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
                Referred Clients &amp; Commissions Ledger
              </h3>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Complete list of businesses registered under your affiliate referral ID.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="text-xs font-semibold gap-1.5 h-9"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Export CSV</span>
              </Button>
              <Button
                size="sm"
                onClick={() => setInviteOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-1.5 h-9"
              >
                <Send className="w-3.5 h-3.5" />
                <span>+ Invite New Partner</span>
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <Card className="p-4 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-80">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by studio name, email, or plan..."
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="pl-9 text-xs h-9"
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-9 rounded-md border px-3 text-xs bg-transparent"
                  style={{ borderColor: "var(--border)", color: "var(--text)" }}
                >
                  <option value="All">All Statuses</option>
                  <option value="Active Paid">Active Paid</option>
                  <option value="Trialing">Trialing</option>
                  <option value="Past Due">Past Due</option>
                </select>
              </div>

              <div className="text-xs text-slate-500 font-semibold">
                Showing {filteredPartners.length} of {referrals.length} Partners
              </div>
            </div>
          </Card>

          {/* Partners Table */}
          <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                    <th className="py-3.5 px-4">Partner / Business</th>
                    <th className="py-3.5 px-4">Contact</th>
                    <th className="py-3.5 px-4">Joined Date</th>
                    <th className="py-3.5 px-4">Subscribed Plan</th>
                    <th className="py-3.5 px-4">Monthly Commission (25%)</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="font-semibold text-sm">No partner clients match this search</p>
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold text-xs flex items-center justify-center">
                              {r.avatar}
                            </div>
                            <div>
                              <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{r.name}</div>
                              <div className="text-xs text-slate-400 font-mono">{r.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-xs font-normal text-slate-600 dark:text-slate-400">
                          {r.contact}
                        </td>
                        <td className="py-3.5 px-4 text-xs text-slate-500">{r.joinedDate}</td>
                        <td className="py-3.5 px-4 text-xs font-semibold text-blue-600">{r.plan}</td>
                        <td className="py-3.5 px-4 font-bold text-emerald-600 text-sm">
                          ${r.commission.toFixed(2)} / mo
                        </td>
                        <td className="py-3.5 px-4">
                          <Badge variant="outline" className={`text-xs ${
                            r.status === "Active Paid" ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
                            "bg-amber-50 text-amber-700 border-amber-300"
                          }`}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                            onClick={() => toast.info(`Viewing details for ${r.name}`)}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 3: MARKETING KIT (Promotional Assets & Templates)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "marketing-kit" && (
        <div className="space-y-8">
          <div>
            <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
              Affiliate &amp; Referral Marketing Kit
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
              Ready-to-use email swipes, social post templates, direct SMS scripts, and media collateral to share your link.
            </p>
          </div>

          {/* Social Posts */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold" style={{ color: "var(--text)" }}>Social Media Announcements</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 border space-y-4 flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      LinkedIn &amp; Professional Network
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border leading-relaxed font-sans" style={{ borderColor: "var(--border)" }}>
                    "We migrated our entire studio pipeline to LaunchDeck Marketing OS. It consolidated our CRM, automated 5-star review collection via SMS, client invoice portals, and marketing funnels into one hub. Highly recommend for any creative agency or photo studio: {referralUrl}"
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs font-semibold gap-1.5 h-9"
                  onClick={() => {
                    navigator.clipboard.writeText(`We migrated our entire studio pipeline to LaunchDeck Marketing OS. It consolidated our CRM, automated 5-star review collection via SMS, client invoice portals, and marketing funnels into one hub. Highly recommend for any creative agency or photo studio: ${referralUrl}`);
                    toast.success("LinkedIn post copied to clipboard!");
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy LinkedIn Post
                </Button>
              </Card>

              <Card className="p-6 border space-y-4 flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                      X (Twitter) &amp; Threads
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border leading-relaxed font-sans" style={{ borderColor: "var(--border)" }}>
                    "Replaced 4 different subscriptions with @LaunchDeckOS. Booking automations + review requests on autopilot. Check it out with our partner link: {referralUrl}"
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs font-semibold gap-1.5 h-9"
                  onClick={() => {
                    navigator.clipboard.writeText(`Replaced 4 different subscriptions with @LaunchDeckOS. Booking automations + review requests on autopilot. Check it out with our partner link: ${referralUrl}`);
                    toast.success("X post copied to clipboard!");
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Tweet / Post
                </Button>
              </Card>
            </div>
          </div>

          {/* Email & SMS Swipes */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold" style={{ color: "var(--text)" }}>Email &amp; Direct Messaging Swipes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 border space-y-4 flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="space-y-3">
                  <div className="font-bold text-sm" style={{ color: "var(--text)" }}>
                    Email Swipe: Studio Peer Recommendation
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border space-y-2 font-sans" style={{ borderColor: "var(--border)" }}>
                    <div className="font-semibold text-slate-800 dark:text-slate-200">Subject: The platform that solved our studio bookings</div>
                    <p className="leading-relaxed">
                      Hey [Name],<br /><br />
                      I wanted to share a quick recommendation. We recently started using LaunchDeck for our client workflows and review automation. It replaced three separate tools and streamlined our invoice deposits.<br /><br />
                      You can test drive it here: {referralUrl}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs font-semibold gap-1.5 h-9"
                  onClick={() => {
                    navigator.clipboard.writeText(`Subject: The platform that solved our studio bookings\n\nHey [Name],\n\nI wanted to share a quick recommendation. We recently started using LaunchDeck for our client workflows and review automation. It replaced three separate tools and streamlined our invoice deposits.\n\nYou can test drive it here: ${referralUrl}`);
                    toast.success("Email swipe copied to clipboard!");
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Email Template
                </Button>
              </Card>

              <Card className="p-6 border space-y-4 flex flex-col justify-between rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="space-y-3">
                  <div className="font-bold text-sm" style={{ color: "var(--text)" }}>
                    SMS / WhatsApp 1-on-1 Script
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border space-y-2 font-sans" style={{ borderColor: "var(--border)" }}>
                    <div className="font-semibold text-slate-800 dark:text-slate-200">Quick Direct Message:</div>
                    <p className="leading-relaxed">
                      "Hey! Thought you'd appreciate LaunchDeck for your marketing retainers and photo bookings. It handles 5-star Google review triggers and automated contracts in one place. Here's our VIP invite link: {referralUrl}"
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs font-semibold gap-1.5 h-9"
                  onClick={() => {
                    navigator.clipboard.writeText(`Hey! Thought you'd appreciate LaunchDeck for your marketing retainers and photo bookings. It handles 5-star Google review triggers and automated contracts in one place. Here's our VIP invite link: ${referralUrl}`);
                    toast.success("SMS script copied to clipboard!");
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy SMS Script
                </Button>
              </Card>
            </div>
          </div>

          {/* Downloadable Collateral & QR Code */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                  <QrCode className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Printable QR Code</h4>
                  <p className="text-xs text-slate-500">Perfect for studio reception desks, business cards, or event booths</p>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border flex flex-col items-center justify-center gap-3" style={{ borderColor: "var(--border)" }}>
                <div className="p-4 bg-white rounded-xl shadow-xs">
                  <QrCode className="w-24 h-24 text-slate-900" />
                </div>
                <span className="text-xs text-slate-500 font-mono">{referralUrl}</span>
              </div>
              <Button
                variant="outline"
                className="w-full text-xs font-semibold gap-1.5"
                onClick={() => {
                  toast.success("High-resolution QR code PNG downloaded!");
                }}
              >
                <Download className="w-3.5 h-3.5" /> Download QR Code (PNG)
              </Button>
            </Card>

            <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Partner Pitch Deck &amp; Brand Assets</h4>
                  <p className="text-xs text-slate-500">12-slide PDF presentation covering features, pricing, and ROI</p>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border space-y-2 text-xs" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between font-semibold">
                  <span>LaunchDeck_Partner_Deck_2026.pdf</span>
                  <Badge variant="outline" className="text-xs">4.2 MB</Badge>
                </div>
                <div className="flex items-center justify-between font-semibold">
                  <span>Social_Media_Banners_Pack.zip</span>
                  <Badge variant="outline" className="text-xs">18.5 MB</Badge>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full text-xs font-semibold gap-1.5"
                onClick={() => {
                  toast.success("Affiliate marketing assets package downloaded!");
                }}
              >
                <Download className="w-3.5 h-3.5" /> Download Media Kit (.ZIP)
              </Button>
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 4: PAYOUT SETTINGS (Banking & Commission Ledger)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "payouts" && (
        <div className="space-y-8">
          <div>
            <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
              Payout Settings &amp; Banking Preferences
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
              Manage bank direct deposit, payout frequency, and past commission receipts.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Active Payout Method Card */}
            <Card className="p-6 border space-y-4 rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Active Payout Account</span>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">Verified &amp; Active</Badge>
              </div>

              <div className="p-4 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white space-y-3 shadow-md">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-slate-400">DIRECT ACH DEPOSIT</div>
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="font-bold text-lg tracking-wider">
                  Chase Bank •••• {payoutConfig.accountLast4}
                </div>
                <div className="text-xs text-slate-400 flex items-center justify-between">
                  <span>Target: {payoutConfig.email}</span>
                  <span className="text-emerald-400 font-semibold">Ready</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full text-xs font-semibold gap-1.5"
                onClick={() => setPayoutOpen(true)}
              >
                <CreditCard className="w-3.5 h-3.5" /> Edit Bank Account
              </Button>
            </Card>

            {/* Payout Schedule & Threshold */}
            <Card className="p-6 border space-y-4 rounded-xl lg:col-span-2" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Commission Payout Rules</h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Payout Schedule</Label>
                  <select
                    className="w-full h-10 rounded-md border px-3 text-sm bg-transparent"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                    defaultValue="1st"
                  >
                    <option value="1st">Monthly on the 1st of every month</option>
                    <option value="biweekly">Bi-weekly (1st and 15th)</option>
                  </select>
                  <span className="text-xs text-slate-400">Next cycle runs Nov 1, 2026</span>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Minimum Payout Threshold</Label>
                  <select
                    value={payoutThreshold}
                    onChange={(e) => setPayoutThreshold(e.target.value)}
                    className="w-full h-10 rounded-md border px-3 text-sm bg-transparent"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                  >
                    <option value="50">$50.00 Minimum</option>
                    <option value="100">$100.00 Minimum (Default)</option>
                    <option value="250">$250.00 Minimum</option>
                    <option value="500">$500.00 Minimum</option>
                  </select>
                  <span className="text-xs text-slate-400">Funds roll over if below threshold</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-900 dark:text-blue-300 space-y-1">
                <span className="font-bold block">Tax &amp; 1099 Status:</span>
                <span>All affiliate payouts are automatically accounted for. An annual Form 1099-NEC is generated electronically if earnings exceed $600/year.</span>
              </div>
            </Card>
          </div>

          {/* Past Payouts History Ledger */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold" style={{ color: "var(--text)" }}>Historical Payouts Ledger</h4>
            <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                      <th className="py-3.5 px-4">Payout ID</th>
                      <th className="py-3.5 px-4">Deposit Date</th>
                      <th className="py-3.5 px-4">Amount</th>
                      <th className="py-3.5 px-4">Method &amp; Account</th>
                      <th className="py-3.5 px-4">Bank Reference</th>
                      <th className="py-3.5 px-4">Status</th>
                      <th className="py-3.5 px-4 text-right">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {payouts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-slate-400">
                          <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="font-semibold text-sm">No Payout History Yet</p>
                          <p className="text-xs text-slate-500 mt-1">Commissions will automatically accumulate here once referred clients subscribe.</p>
                        </td>
                      </tr>
                    ) : (
                      payouts.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                          <td className="py-3.5 px-4 font-mono text-xs font-semibold text-blue-600">{p.id}</td>
                          <td className="py-3.5 px-4 text-xs text-slate-600 dark:text-slate-400">{p.date}</td>
                          <td className="py-3.5 px-4 font-bold text-emerald-600 text-sm">{p.amount}</td>
                          <td className="py-3.5 px-4 text-xs text-slate-500">
                            {p.method} ({p.account})
                          </td>
                          <td className="py-3.5 px-4 font-mono text-xs text-slate-400">{p.ref}</td>
                          <td className="py-3.5 px-4">
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5" /> {p.status}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-blue-600 hover:text-blue-800 font-semibold gap-1"
                              onClick={() => handleDownloadReceipt(p)}
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Receipt</span>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── Edit Custom Referral Code Modal ── */}
      <Dialog open={editCodeOpen} onOpenChange={setEditCodeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Referral Code</DialogTitle>
            <DialogDescription>
              Create a personalized affiliate code for your agency or creative brand.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCustomCode} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Custom Code (Letters, Numbers, Hyphen)</Label>
              <Input
                value={customCodeInput}
                onChange={(e) => setCustomCodeInput(e.target.value.toUpperCase())}
                placeholder="MYSTUDIO-2026"
                className="mt-1.5 font-mono text-sm uppercase font-bold"
                maxLength={20}
                required
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Your link will be: <span className="font-mono text-blue-500">deck.weddingsthyzfoto.com/signup?ref={customCodeInput.toLowerCase() || "code"}</span>
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setEditCodeOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingCode} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                {savingCode ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
                Save Partner Code
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Send Invite Modal ── */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Referral Invitation</DialogTitle>
            <DialogDescription>
              Invite a client or agency partner to sign up under your 25% recurring commission link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendInvite} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Partner / Client Name</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="e.g. David Miller"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Delivery Channel</Label>
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    checked={inviteForm.channel === "SMS"}
                    onChange={() => setInviteForm({ ...inviteForm, channel: "SMS" })}
                  />
                  <span>SMS Text (Twilio)</span>
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    checked={inviteForm.channel === "Email"}
                    onChange={() => setInviteForm({ ...inviteForm, channel: "Email" })}
                  />
                  <span>Email</span>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">
                {inviteForm.channel === "SMS" ? "Phone Number" : "Email Address"}
              </Label>
              <Input
                value={inviteForm.contact}
                onChange={(e) => setInviteForm({ ...inviteForm, contact: e.target.value })}
                placeholder={inviteForm.channel === "SMS" ? "+1 (555) 000-0000" : "partner@agency.com"}
                className="mt-1 text-sm font-mono"
                required
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={sendingInvite} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                {sendingInvite ? "Dispatching…" : "Dispatch Referral Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Payout Settings Modal ── */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Referral Commission Payout Settings</DialogTitle>
            <DialogDescription>
              Configure how you receive your monthly recurring commission payouts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Payout Method</Label>
              <select
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value)}
                className="w-full mt-1.5 h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option value="Stripe Direct Deposit">Stripe Direct Bank Deposit (ACH)</option>
                <option value="PayPal Wallet">PayPal Business Wallet</option>
                <option value="Wire Transfer">Direct Wire Transfer</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Payout Account Email / ID</Label>
              <Input
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                className="mt-1.5 font-mono text-sm"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setPayoutOpen(false)}>Cancel</Button>
              <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm" onClick={handleSavePayouts}>
                Save Payout Settings
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── QR Code Preview Modal ── */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm text-center">
          <DialogHeader>
            <DialogTitle>Scan to Join Affiliate Network</DialogTitle>
            <DialogDescription>
              Clients can scan this code to register under your account.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 flex flex-col items-center gap-4">
            <div className="p-4 bg-white rounded-2xl shadow-md border border-slate-200">
              <QrCode className="w-40 h-40 text-slate-900" />
            </div>
            <span className="font-mono text-xs text-slate-500 break-all">{referralUrl}</span>
          </div>
          <DialogFooter>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm"
              onClick={() => {
                toast.success("High-res QR Code downloaded!");
                setQrOpen(false);
              }}
            >
              <Download className="w-4 h-4 mr-1.5" /> Download QR Code PNG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
